import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft,
  ArrowLeft,
  RefreshCw,
  Check,
  ArrowRight,
  Lightbulb,
  Heart
} from "lucide-react";

const REFRAME_PROMPTS = [
  "What evidence do I have for and against this thought?",
  "Would I say this to a friend in the same situation?",
  "What's a more balanced way to look at this?",
  "What would I tell myself if I were being kind?",
  "Is this thought helpful or harmful to me?"
];

export default function ThoughtReframePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [negativeThought, setNegativeThought] = useState("");
  const [reframedThought, setReframedThought] = useState("");
  const [step, setStep] = useState("negative"); // negative, reframe, complete
  const [saving, setSaving] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(0);

  const handleContinue = () => {
    if (negativeThought.trim()) {
      setStep("reframe");
    }
  };

  const nextPrompt = () => {
    setCurrentPrompt((prev) => (prev + 1) % REFRAME_PROMPTS.length);
  };

  const handleSave = async () => {
    if (!negativeThought.trim() || !reframedThought.trim()) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/journal`,
        {
          title: "Thought Reframe",
          body: `**Original thought:** ${negativeThought}\n\n**Reframed thought:** ${reframedThought}`,
          tags: ["reframe", "cognitive-reframing"],
          source: "reframe_tool"
        },
        { 
          withCredentials: true 
        }
      );
      setStep("complete");
      toast.success("Thought reframe saved!");
    } catch (error) {
      console.error("Failed to save reframe:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    navigate("/compass");
  };

  const handleStartNew = () => {
    setNegativeThought("");
    setReframedThought("");
    setStep("negative");
    setCurrentPrompt(0);
  };

  return (
    <div className="min-h-screen bg-[#0b2821] pb-24" data-testid="thought-reframe-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0b2821]/95 backdrop-blur-sm border-b border-[#a69373]/20">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate("/compass")}
            className="p-2 -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-[#a69373]" />
            </div>
            <h1 className="text-lg font-heading text-[#a69373]">Thought Reframe</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-6">
        {step === "negative" && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-heading text-white mb-2">Capture the Thought</h2>
              <p className="text-white/60">Write down a negative or unhelpful thought you're having</p>
            </div>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <label className="text-white/60 text-sm mb-2 block">
                  What's the thought that's bothering you?
                </label>
                <Textarea
                  value={negativeThought}
                  onChange={(e) => setNegativeThought(e.target.value)}
                  placeholder="I always mess things up... / I'm not good enough... / Everyone is judging me..."
                  className="min-h-[150px] bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  data-testid="negative-thought-input"
                />
              </CardContent>
            </Card>

            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Lightbulb className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white/80 text-sm font-medium mb-1">Tip</p>
                  <p className="text-white/50 text-sm">
                    Be specific about the thought. Instead of "I'm anxious," try "I'm worried that I'll fail my presentation tomorrow."
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleContinue}
              disabled={!negativeThought.trim()}
              className="w-full bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
              data-testid="continue-button"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {step === "reframe" && (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-heading text-white mb-2">Reframe It</h2>
              <p className="text-white/60">Challenge the thought and find a more balanced perspective</p>
            </div>

            {/* Original thought display */}
            <Card className="bg-red-500/10 border-red-500/20">
              <CardContent className="p-4">
                <p className="text-red-300/70 text-xs uppercase tracking-wide mb-1">Original Thought</p>
                <p className="text-white/80 text-sm">{negativeThought}</p>
              </CardContent>
            </Card>

            {/* Reframe prompt */}
            <div className="bg-[#a69373]/10 rounded-xl p-4 border border-[#a69373]/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[#a69373] text-xs uppercase tracking-wide">Ask Yourself</p>
                <button 
                  onClick={nextPrompt}
                  className="text-[#a69373] text-xs hover:underline"
                >
                  Try another →
                </button>
              </div>
              <p className="text-white font-medium">{REFRAME_PROMPTS[currentPrompt]}</p>
            </div>

            {/* Reframed thought input */}
            <Card className="bg-green-500/10 border-green-500/20">
              <CardContent className="p-4">
                <label className="text-green-300/70 text-xs uppercase tracking-wide mb-2 block">
                  Reframed Thought
                </label>
                <Textarea
                  value={reframedThought}
                  onChange={(e) => setReframedThought(e.target.value)}
                  placeholder="Write a kinder, more balanced version of the thought..."
                  className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  data-testid="reframed-thought-input"
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("negative")}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !reframedThought.trim()}
                className="flex-1 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
                data-testid="save-reframe-button"
              >
                {saving ? "Saving..." : "Save Reframe"}
              </Button>
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <Heart className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">Great Job!</h2>
            <p className="text-white/60 mb-6">
              You've practiced cognitive reframing. This skill gets stronger with practice.
            </p>

            <Card className="bg-white/5 border-white/10 mb-8 text-left">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-red-300/70 text-xs uppercase tracking-wide mb-1">Before</p>
                  <p className="text-white/60 text-sm">{negativeThought}</p>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <p className="text-green-300/70 text-xs uppercase tracking-wide mb-1">After</p>
                  <p className="text-white text-sm">{reframedThought}</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleStartNew}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Reframe Another
              </Button>
              <Button
                onClick={handleFinish}
                className="flex-1 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
                data-testid="finish-button"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
